import {
 Button, Divider, Icon, Menu
} from '@nike/eds';
import { addDays } from 'date-fns';
import moment from 'moment';
import PropTypes from 'prop-types';
/* import { translate } from 'rcffus-sps-translations'; */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setNposValue, setNposErrors, setNposFilters } from '../../../../redux/actions';
import { getBodyTranslations } from '../../../../redux/selectors';
import { MOMENT_DATE_FORMAT, NPOS } from '../../../../utils/constants';
import { getFilterLabelText } from '../nposUtils';
import Filter from '../reusable/Filter';

import DateRangePicker from './DateRangePicker';
import { convertDateToTimezone } from './DateRangePicker/dateUtils';

const PurchaseDate = ({
 handleChange, timezone, resetSearch, open, setOpen
}) => {
  const [isDateSelected, setIsDateSelected] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [dirtyFlag, setDirtyFlag] = useState(false);

  const dispatch = useDispatch();

  const error = useSelector((state) => state.nposErrors);
  const translations = useSelector(getBodyTranslations);

  const [timeRange, setTimeRange] = useState({ from: NPOS.DAY_START_TIME, to: NPOS.DAY_END_TIME });

  const [dateRange, setDateRange] = useState([
    {
      endDate: addDays(new Date(moment.tz(timezone).format(MOMENT_DATE_FORMAT)), 1),
      key: 'selection',
      startDate: addDays(new Date(moment.tz(timezone).format(MOMENT_DATE_FORMAT)), 1),
    }
  ]);

  useEffect(() => {
    dispatch(setNposValue({ openFilter: open ? NPOS.FILTERS.PURCHASE_DATE : null }));
  }, [open, dispatch]);

  useEffect(() => {
      setDirtyFlag(true);
      setDateRange([{
        endDate: addDays(new Date(moment.tz(timezone).format(MOMENT_DATE_FORMAT)), 1),
        key: 'selection',
        startDate: addDays(new Date(moment.tz(timezone).format(MOMENT_DATE_FORMAT)), 1),
      }]);
      setIsFilterApplied(false);
      setTimeRange({ from: NPOS.DAY_START_TIME, to: NPOS.DAY_END_TIME });
      setIsDateSelected(false);
  }, [resetSearch, timezone]);

  useEffect(() => {
    const setError = (value) => dispatch(setNposErrors({ selectedDates: value }));

    const setOrResetError = (errorCode, isError = true) => {
      if (!NPOS.ERRORS[errorCode]) { setError({ code: 'UNKNOWN_ERROR', message: 'Unknown error occurred' }); }

      return setError(isError ? { code: errorCode, message: translations[NPOS.ERRORS[errorCode]] } : null);
    };

    const validateDateRange = () => {
      if (!dateRange?.[0]) return false;

      const startDateWithTime = moment.utc(`${moment(dateRange[0].startDate).format(MOMENT_DATE_FORMAT)} ${timeRange.from.value}`).format();
      const endDateWithTime = moment.utc(`${moment(dateRange[0].endDate).format(MOMENT_DATE_FORMAT)} ${timeRange.to.value}`).format();

      if (moment.utc(startDateWithTime).isSame(endDateWithTime, 'day') && moment.utc(endDateWithTime).isBefore(startDateWithTime)) {
        return setOrResetError(NPOS.ERROR_CODES.STARTIME_GT_ENDTIME);
      } else if (moment.utc(startDateWithTime).isAfter(endDateWithTime)) {
        return setOrResetError(NPOS.ERROR_CODES.STARTDATE_GT_ENDDATE);
      }

      return setOrResetError(null, false); // resets the error
    };

    validateDateRange();
  }, [timeRange, dateRange, translations, dispatch]);

  const timeOnChange = (val, field) => {
    const newTimeRange = { ...timeRange, [field]: val };
    setDirtyFlag(true);
    setTimeRange(newTimeRange);
  };

  const updateDateFilter = (obj) => {
    const convertedDate = convertDateToTimezone(obj, timeRange, timezone, moment);
    setIsFilterApplied(isDateSelected);

    dispatch(setNposFilters({ selectedDates: convertedDate }));

    handleChange('selectedDates', convertedDate);
    return true;
  };

  const dateRangePickerOnChange = (item) => {
    setIsDateSelected(true);
    setDirtyFlag(true);
    setDateRange([item.selection]);
  };

  const onDateCommit = () => {
    if (isDateSelected && dirtyFlag) updateDateFilter(dateRange[0]);
    setOpen(false);
  };

  const onClearFilterClick = () => {
    dispatch(setNposFilters({ selectedDates: null }));
    setDirtyFlag(true);
    setDateRange([
      {
        endDate: addDays(new Date(moment.tz(timezone).format(MOMENT_DATE_FORMAT)), 1),
        key: 'selection',
        startDate: addDays(new Date(moment.tz(timezone).format(MOMENT_DATE_FORMAT)), 1),
      }
    ]);
    setIsFilterApplied(false);
    setTimeRange({ from: NPOS.DAY_START_TIME, to: NPOS.DAY_END_TIME });
    setIsDateSelected(false);
  };

  const Body = (
    <div
      id="date-picker"
      style={{
          display: 'flex', flexDirection: 'column', margin: '10px', padding: '10px', paddingTop: '4px'
        }}
    >
      <DateRangePicker
        dateRangePickerOnChange={dateRangePickerOnChange}
        ranges={dateRange}
        timeOnChange={timeOnChange}
        timeRange={timeRange}
        timezone={timezone}
        translations={translations}
      />
      {error.selectedDates && (
      <div>
        <p className="d-sm-flx flx-jc-sm-c flx-ai-sm-c text-color-error" style={{ gap: '10px' }}>
          <Icon name="AlertCircleFilled" size="m" /> {error.selectedDates.message}
        </p>
      </div>
      )}
      <Divider vertical />
      <div className="p2-sm" style={{ display: 'flex', gap: '20px', justifyContent: 'flex-end' }}>
        {isDateSelected && (
          <Button
            data-testid="purchased-date-filter-clear-button"
            size="small"
            variant="ghost"
            onClick={onClearFilterClick}
          >
            {translations.RL_ClearFilter}
          </Button>
        )}
        <Button
          data-testid="purchased-date-filter-done-button"
          size="small"
          variant="primary"
          onClick={onDateCommit}
        >
          {translations.RL_Done}
        </Button>
      </div>
    </div>
    );

  const labelText = getFilterLabelText(NPOS.FILTERS.PURCHASE_DATE, { dateRange: dateRange?.[0] || {}, isDateSelected, moment }, translations);

  const openFilterMenu = (value) => {
    setDirtyFlag(false);
    setOpen(value);
  };

  return (
    <Menu
      bodySlot={Body}
      className="npos-filter"
      isOpen={open}
      placement="bottom"
      onClickOutside={onDateCommit}
    >
      <Filter
        filterId={NPOS.FILTERS.PURCHASE_DATE}
        isError={error.selectedDates}
        isFilterApplied={isFilterApplied}
        label={labelText}
        open={open}
        setOpen={openFilterMenu}
      />
    </Menu>
  );
};

PurchaseDate.propTypes = {
  handleChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  resetSearch: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired
};

export default PurchaseDate;
